/**
 * What-If scenario calculation hooks
 */
import { useQuery } from '@tanstack/react-query'
import { analyticsApi } from '../api/analytics'
import { useHoldings } from './useHoldings'
import type {
  BuySimulatorInput,
  BuySimulatorResult,
  PositionSizingInput,
  PositionSizingResult,
  AllocationTarget,
  RebalanceTrade,
  RebalanceResult,
} from '../types'

// ============ Price Lookup Hook ============

export function usePrice(symbol: string | null) {
  return useQuery({
    queryKey: ['price', symbol],
    queryFn: () => analyticsApi.getPrice(symbol!),
    enabled: !!symbol && symbol.length > 0,
    staleTime: 60000, // 1 minute
  })
}

// ============ Buy Simulator Hook ============

export function useBuySimulator() {
  const { data: holdings = [] } = useHoldings()

  const calculate = (input: BuySimulatorInput): BuySimulatorResult => {
    const { symbol, shares, pricePerShare } = input
    const totalCost = shares * pricePerShare

    // Calculate current portfolio value
    const currentPortfolioValue = holdings.reduce((sum, h) => {
      return sum + (h.current_value ?? h.cost_basis)
    }, 0)

    // Find existing holding for this symbol (sum across accounts)
    const existingHoldings = holdings.filter(h =>
      h.symbol.toUpperCase() === symbol.toUpperCase()
    )
    const currentSymbolValue = existingHoldings.reduce((sum, h) => {
      return sum + (h.current_value ?? h.cost_basis)
    }, 0)
    const currentSymbolAllocation = currentPortfolioValue > 0
      ? (currentSymbolValue / currentPortfolioValue) * 100
      : 0

    // After buy state
    const newPortfolioValue = currentPortfolioValue + totalCost
    const newSymbolValue = currentSymbolValue + totalCost
    const newSymbolAllocation = newPortfolioValue > 0
      ? (newSymbolValue / newPortfolioValue) * 100
      : 0

    // Diversification impact
    const allocationChange = newSymbolAllocation - currentSymbolAllocation
    let diversificationImpact: 'increases' | 'decreases' | 'neutral' = 'neutral'

    // If adding to largest position, decreases diversification
    // If adding to smaller position, increases diversification
    const sortedHoldings = [...holdings].sort((a, b) =>
      (b.current_value ?? b.cost_basis) - (a.current_value ?? a.cost_basis)
    )
    const largestSymbol = sortedHoldings[0]?.symbol

    if (largestSymbol?.toUpperCase() === symbol.toUpperCase() && allocationChange > 1) {
      diversificationImpact = 'decreases'
    } else if (currentSymbolValue === 0 && holdings.length > 0) {
      diversificationImpact = 'increases'
    } else if (allocationChange < 0) {
      diversificationImpact = 'increases'
    }

    return {
      symbol: symbol.toUpperCase(),
      shares,
      pricePerShare,
      totalCost,
      currentPortfolioValue,
      currentSymbolValue,
      currentSymbolAllocation,
      newPortfolioValue,
      newSymbolValue,
      newSymbolAllocation,
      allocationChange,
      diversificationImpact,
    }
  }

  return { calculate, holdings }
}

// ============ Position Sizing Hook ============

export function usePositionSizing() {
  const { data: holdings = [] } = useHoldings()

  const calculate = (input: PositionSizingInput): PositionSizingResult => {
    const { symbol, pricePerShare, riskPercentage, stopLossPercentage } = input

    // Calculate current portfolio value
    const portfolioValue = holdings.reduce((sum, h) => {
      return sum + (h.current_value ?? h.cost_basis)
    }, 0)

    // Risk amount is % of portfolio
    const riskAmount = portfolioValue * (riskPercentage / 100)

    // Stop loss price
    const stopLossPrice = pricePerShare * (1 - stopLossPercentage / 100)
    const lossPerShare = pricePerShare - stopLossPrice

    // Position size: risk amount / loss per share
    const maxShares = lossPerShare > 0 ? Math.floor(riskAmount / lossPerShare) : 0
    const positionValue = maxShares * pricePerShare
    const positionAllocation = portfolioValue > 0
      ? (positionValue / portfolioValue) * 100
      : 0

    return {
      symbol: symbol.toUpperCase(),
      pricePerShare,
      riskPercentage,
      stopLossPercentage,
      portfolioValue,
      riskAmount,
      stopLossPrice,
      lossPerShare,
      maxShares,
      positionValue,
      positionAllocation,
    }
  }

  return { calculate, holdings }
}

// ============ Rebalance Preview Hook ============

export function useRebalancePreview() {
  const { data: holdings = [] } = useHoldings()

  const calculate = (
    targets: AllocationTarget[],
    groupBy: 'portfolio' | 'tag'
  ): RebalanceResult => {
    const trades: RebalanceTrade[] = []
    const warnings: string[] = []
    let totalBuyValue = 0
    let totalSellValue = 0

    // Group holdings by portfolio or tag
    const holdingsByGroup = new Map<string, typeof holdings>()

    for (const holding of holdings) {
      if (groupBy === 'portfolio') {
        const key = holding.portfolio_id
        if (!holdingsByGroup.has(key)) {
          holdingsByGroup.set(key, [])
        }
        holdingsByGroup.get(key)!.push(holding)
      } else {
        // Tag grouping - holdings can be in multiple tags
        for (const tagName of holding.tags) {
          if (!holdingsByGroup.has(tagName)) {
            holdingsByGroup.set(tagName, [])
          }
          holdingsByGroup.get(tagName)!.push(holding)
        }
        // Untagged holdings
        if (holding.tags.length === 0) {
          if (!holdingsByGroup.has('untagged')) {
            holdingsByGroup.set('untagged', [])
          }
          holdingsByGroup.get('untagged')!.push(holding)
        }
      }
    }

    // Calculate total portfolio value
    const totalValue = holdings.reduce((sum, h) =>
      sum + (h.current_value ?? h.cost_basis), 0
    )

    // For each target, determine required trades
    for (const target of targets) {
      const diff = target.targetValue - target.currentValue

      if (Math.abs(diff) < 1) continue // Skip tiny differences

      const groupHoldings = holdingsByGroup.get(target.id) ?? []

      if (diff > 0) {
        // Need to buy - but we can only suggest, not specify which symbol
        warnings.push(
          `Need to add $${diff.toFixed(0)} to "${target.name}" - select symbols manually`
        )
        totalBuyValue += diff
      } else {
        // Need to sell from this group
        // Proportionally sell from each holding in the group
        const sellAmount = Math.abs(diff)
        const groupValue = groupHoldings.reduce((sum, h) =>
          sum + (h.current_value ?? h.cost_basis), 0
        )

        for (const holding of groupHoldings) {
          const holdingValue = holding.current_value ?? holding.cost_basis
          const holdingProportion = groupValue > 0 ? holdingValue / groupValue : 0
          const sellFromHolding = sellAmount * holdingProportion

          if (sellFromHolding < 1) continue

          const pricePerShare = holding.current_price ??
            (holding.shares > 0 ? holdingValue / holding.shares : 0)
          const sharesToSell = pricePerShare > 0
            ? Math.floor(sellFromHolding / pricePerShare)
            : 0

          if (sharesToSell > 0) {
            const tradeValue = sharesToSell * pricePerShare
            trades.push({
              symbol: holding.symbol,
              action: 'sell',
              shares: sharesToSell,
              pricePerShare,
              tradeValue,
              fromAllocation: (holdingValue / totalValue) * 100,
              toAllocation: ((holdingValue - tradeValue) / (totalValue - sellAmount)) * 100,
            })
            totalSellValue += tradeValue
          }
        }
      }
    }

    const netCashFlow = totalBuyValue - totalSellValue
    const rebalanceAchievable = netCashFlow <= 0 || warnings.length === 0

    return {
      groupBy,
      trades,
      totalBuyValue,
      totalSellValue,
      netCashFlow,
      rebalanceAchievable,
      warnings,
    }
  }

  return { calculate, holdings }
}
